import { observable, computed, action, reaction } from 'mobx';
import { app } from './../App';
import { debounce } from 'lodash';
import { toFixed, unformat } from 'accounting';
import * as moment from 'moment';
import {
    ISearchInputValues,
    ISearchItemInfo,
    ISearchItem,
    ISearchFitments,
    ICartInfo,
    ICartInfoItem,
    ISearchItemsGroup
} from './../../../common/interfaces/search';
import { user, categories } from './AllStores';
import { MakeModel } from '../../../common/helpers/makeModel';

// Store
export class Search {
    constructor() {
        this.getItemsDebounce = debounce(this.getItemsDebounced, 1024);
    }

    private reactToUserChange = reaction(
        () => user.id,
        id => {
            if (id === null) {
                this.clearSearchInput();
            }
        }
    );

    private minFieldCharactersCount: number = 2;

    @observable public searchItems: ISearchItem[] = [];
    @observable public searchItemsTotalCount: number = 0;
    @observable public searchItemsLoadDateTime: moment.Moment = moment();

    @computed
    public get searchItemsIds(): number[] {
        return this.searchItems.map(item => item.id);
    }

    @computed
    public get searchItemsGroupedByCar(): ISearchItemsGroup[] {
        const groups: ISearchItemsGroup[] = [];

        this.searchItems.map(item => {
            const concat = MakeModel.concatFromYearToYearMakeModel(item.fromYear, item.toYear, item.make, item.model);

            let groupExists: number = -1;
            groups.some((group, groupIndex) => {
                if (group.concat === concat) {
                    groupExists = groupIndex;
                    return true;
                }
            });

            if (groupExists !== -1) {
                groups[groupExists].items.push(item);
            } else {
                groups.push({
                    concat: concat,
                    fromYear: item.fromYear,
                    toYear: item.toYear,
                    make: item.make,
                    model: item.model,
                    items: [item]
                });
            }
        });

        return groups;
    }

    @observable public searchFetched: boolean = false;
    @observable public searchTemporaryLoading: number = 0;
    @observable public searchLoadingResults: number = 0;

    @computed
    public get searchLoading(): boolean {
        return this.searchTemporaryLoading > 0 || this.searchLoadingResults > 0;
    }

    @observable public searchFetchLimit: number = 20;
    @observable public searchFetchOffset: number = 0;

    @computed
    public get searchItemsPage(): number {
        return this.searchFetchOffset % (this.searchFetchLimit - 1) + 1;
    }

    @observable public appliedCategoriesAndCertificatesFilterCount: number = 0;
    @observable
    public searchInputValues: ISearchInputValues = {
        makeModelYear: '',
        partNumber: '',
        customerItemCode: '',
        itemGroup: [],
        certCategory: [],
        modified: false,
        modifiedGroups: false
    };

    @computed
    public get searchInputEntered(): boolean {
        return (
            this.searchInputValues.makeModelYear.trim().length >= this.minFieldCharactersCount ||
            this.searchInputValues.customerItemCode.trim().length >= this.minFieldCharactersCount ||
            this.searchInputValues.partNumber.trim().length >= this.minFieldCharactersCount
        );
    }

    @computed
    public get prepareCategoryString(): string {
        let str = '';
        const groups = this.searchInputValues.itemGroup
            .reverse()
            .map(group => group)
            .join(', ');
        const certifications = this.searchInputValues.certCategory
            .reverse()
            .map(cert => cert)
            .join(', ');

        str += groups || '';
        str += certifications ? (groups ? ', ' : '') + 'Cert. ' + certifications : '';

        return str;
    }

    private getItemsDebounce = null;
    private getItemsDebounced = () => {
        this.getItems();
    };

    private reactToSearchInputValuesModified = reaction(
        () => this.searchInputValues.modified,
        (modified: boolean) => {
            if (modified) {
                this.searchFetchOffset = 0;
                this.searchTemporaryLoading++;
                this.getItemsDebounce();
            }
        }
    );

    private reactToSearchFiltersModified = reaction(
        () => this.searchInputValues.modifiedGroups,
        (modifiedGroups: boolean) => {
            if (modifiedGroups) {
                this.searchFetchOffset = 0;
                this.getItems();
            }
        }
    );

    @computed
    public get searchItemsLength(): number {
        return this.searchItems.length;
    }

    @computed
    public get searchMMY(): string | null {
        return this.searchItemsLength ? `${this.getYearsRange} ${this.searchItems[0].make} ${this.searchItems[0].model}` : null;
    }

    @computed
    public get getYearsRange(): string | null {
        let range = this.searchItems
            .map(v => v.toYear)
            .slice()
            .sort((a, b) => a - b);
        return `${range[0]} - ${range[range.length - 1]}`;
    }

    @action
    public clearSearchInput = () => {
        this.searchItems = [];
        this.searchInputValues = {
            makeModelYear: '',
            partNumber: '',
            customerItemCode: '',
            modified: false,
            itemGroup: [],
            certCategory: [],
            modifiedGroups: false
        };
    };

    @action
    public changeItemsPage = (newPage: number) => {
        this.searchFetchOffset = (newPage - 1) * this.searchFetchLimit;
        this.getItems();
    };

    @action
    public getItems = async (): Promise<ISearchItem[]> => {
        try {
            this.searchLoadingResults++;

            if (this.searchInputEntered) {
                const searchStartTime = moment();
                this.searchInputValues.modified = false;
                this.searchInputValues.modifiedGroups = false;

                const service: any = app.service('item-search');
                const items = (await service.find({
                    query: {
                        makeModelYear:
                            this.searchInputValues.makeModelYear.trim().length >= this.minFieldCharactersCount
                                ? this.searchInputValues.makeModelYear.trim().toLowerCase()
                                : undefined,
                        partNumber:
                            this.searchInputValues.partNumber.trim().length >= this.minFieldCharactersCount
                                ? this.searchInputValues.partNumber.trim()
                                : undefined,
                        customerItemCode:
                            this.searchInputValues.customerItemCode.trim().length >= this.minFieldCharactersCount
                                ? this.searchInputValues.customerItemCode.trim()
                                : undefined,
                        itemGroup:
                            this.searchInputValues.itemGroup.length > 0
                                ? JSON.stringify(this.searchInputValues.itemGroup)
                                : undefined,
                        certCategory:
                            this.searchInputValues.certCategory.length > 0
                                ? JSON.stringify(this.searchInputValues.certCategory)
                                : undefined,
                        fitments: 1,
                        limit: this.searchFetchLimit,
                        skip: this.searchFetchOffset
                    }
                })) as ISearchItemInfo;

                if (searchStartTime.isAfter(this.searchItemsLoadDateTime)) {
                    this.searchItemsLoadDateTime = searchStartTime;
                    this.searchItemsTotalCount = items.total;
                    this.getItemsDebounce.cancel;

                    categories.categoriesListFromSearch = items.itemGroup.map(group => group.itemGroup);
                    categories.certificationListFromSearch = items.certCategory.map(category => category.certCategory);

                    this.searchItems = items.data.map((item, key) => ({
                        ...item,
                        price: toFixed(unformat(item.price), 2),
                        listPrice: toFixed(unformat(item.listPrice), 2),
                        stockTX:
                            typeof item.stockTX !== 'undefined' && item.stockTX !== null
                                ? toFixed(unformat(item.stockTX), 0)
                                : '0',
                        stockVA:
                            typeof item.stockVA !== 'undefined' && item.stockVA !== null
                                ? toFixed(unformat(item.stockVA), 0)
                                : '0',
                        stockCA:
                            typeof item.stockCA !== 'undefined' && item.stockCA !== null
                                ? toFixed(unformat(item.stockCA), 0)
                                : '0',
                        LocalStock:
                            typeof item.LocalStock !== 'undefined' && item.LocalStock !== null
                                ? toFixed(unformat(item.LocalStock), 0)
                                : '0',
                        RegionalStock:
                            typeof item.RegionalStock !== 'undefined' && item.RegionalStock !== null
                                ? toFixed(unformat(item.RegionalStock), 0)
                                : '0',
                        fitments: typeof item.lines !== 'undefined' && item.lines ? item.lines : [],
                        showFitments: false
                    }));
                }

                return this.searchItems;
            } else {
                this.searchInputValues.modified = false;
                this.searchInputValues.modifiedGroups = false;
                return [];
            }
        } catch (err) {
            console.error(err);
            return err;
        } finally {
            this.searchFetched = true;
            this.searchLoadingResults--;

            if (this.searchTemporaryLoading > 0) {
                this.searchTemporaryLoading--;
            }
        }
    };

    @action
    public toggleFitments = (itemId: number, showFitments?: boolean) => {
        let itemFound = this.searchItemsIds.indexOf(itemId);
        if (itemFound !== -1) {
            showFitments = typeof showFitments !== 'undefined' ? showFitments : !this.searchItems[itemFound].showFitments;
            this.searchItems[itemFound].showFitments = showFitments;
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Search.ts